import {ReactElement, useEffect} from "react";
import {useBulkAddDriversMutation, useLoadSingleDriverOperationalNoteLazyQuery, VersionType} from "../../../__generated__/generated_types";
import {transformMinMaxValueToDecimalString} from "../../workflows/account/formula-bar/components/formula-nodes/components/MinMaxFields";
import {CopyDriverModal, CopyDriverModalOnSubmitArgs} from "./CopyDriverModal";


export interface CopyOperationalDriverSourceDriver {
    driverId: string;
    name: string;
    propertyId: string;
    propertyName: string;
    destinationAccountId: string;
    accountLabel: string;
    operationalMetricTypeId: string;
    secondaryOperationalMetricTypeId: string | null;
    status: string;
    locked: boolean;
    valuesLocked: boolean;
    configLocked: boolean;
    amounts: (number | null)[];
    percents: (number | null)[];
    delayRecognition: number;
    cumSum: boolean | null;
    nonProrated: boolean;
    minValue: number | string | null;
    maxValue: number | string | null;
}

export interface CopyOperationalDriverAssumptionsModalProps {
    visible: boolean;
    budgetYear: number;
    versionType: VersionType.Budget | VersionType.Reforecast;
    sourceDriver: CopyOperationalDriverSourceDriver;
    destinationProperties: {id: string; name: string, disabled?: boolean}[];
    defaultSelectedProperties?: string[];
    accounts: {id: string; label: string}[];
    onClose: () => void;
    onCopied: () => void;
}

export function CopyOperationalDriverAssumptionsModal(props: CopyOperationalDriverAssumptionsModalProps): ReactElement {
    const {
        visible,
        budgetYear,
        versionType,
        sourceDriver,
        destinationProperties,
        defaultSelectedProperties,
        accounts,
        onClose,
        onCopied
    } = props;

    const [bulkAddDrivers, {loading: copyLoading}] = useBulkAddDriversMutation({notifyOnNetworkStatusChange: true});
    const [fetchNote, {data: noteData, loading: noteLoading}] = useLoadSingleDriverOperationalNoteLazyQuery({fetchPolicy: "no-cache"});

    useEffect(() => {
        fetchNote({
            variables: {
                driverOperationalId: sourceDriver.driverId,
            }
        });
    }, [sourceDriver.driverId]);

    const handleCopy = async (args: CopyDriverModalOnSubmitArgs) => {
        const {
            copyNote,
            copyStatus,
            selectedPropertyIds,
            selectedAccountIds,
        } = args;

        if (noteLoading) {
            return;
        }

        let note = null;
        let noteReadOnly = false;
        if (copyNote === true && typeof noteData?.loadSingleDriverOperationalNote?.note === "string" && noteData?.loadSingleDriverOperationalNote?.note.trim() !== "") {
            note = noteData?.loadSingleDriverOperationalNote?.note;
            noteReadOnly = noteData?.loadSingleDriverOperationalNote?.isReadOnly;
        }

        const transformedAmounts = sourceDriver.amounts.map(a => a == null ? "0" : String(a));
        const transformedPercents = sourceDriver.percents.map(p => p == null ? "0" : String(p));
        const result = await bulkAddDrivers({
            variables: {
                propertyIds: selectedPropertyIds,
                accountIds: selectedAccountIds,
                year: versionType === VersionType.Reforecast ? budgetYear - 1 : budgetYear,
                versionType: versionType,
                overwriteOnDuplicate: true,
                operational: {
                    delayRecognition: sourceDriver.delayRecognition,
                    operationalMetricTypeId: sourceDriver.operationalMetricTypeId,
                    secondaryOperationalMetricTypeId: sourceDriver.secondaryOperationalMetricTypeId,
                    monthlyPercents: transformedPercents,
                    monthlyAmounts: transformedAmounts,
                    name: sourceDriver.name,
                    cumSum: sourceDriver.cumSum,
                    nonProrated: sourceDriver.nonProrated,
                    minValue: transformMinMaxValueToDecimalString(sourceDriver.minValue),
                    maxValue: transformMinMaxValueToDecimalString(sourceDriver.maxValue),
                    locked: sourceDriver.locked,
                    valuesLocked: sourceDriver.valuesLocked,
                    configLocked: sourceDriver.configLocked,
                    note: note,
                    noteReadOnly: noteReadOnly,
                },
                copyNote: copyNote,
                copyStatus: copyStatus,
            }
        });
        return result.data?.bulkAddDrivers;
    };

    return (
        <CopyDriverModal
            visible={visible}
            sourceDriver={sourceDriver}
            destinationProperties={destinationProperties}
            defaultSelectedProperties={defaultSelectedProperties}
            accounts={accounts}
            note={noteData?.loadSingleDriverOperationalNote?.note}
            noteLoading={noteLoading}
            copyBusy={copyLoading}
            onSubmit={handleCopy}
            onClose={onClose}
            onCopied={onCopied}
        />
    );
}
